import React from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { HiArrowNarrowRight } from "react-icons/hi";
import {
  HiChevronRight,
  HiOutlineChartBar,
  HiOutlineEnvelope,
  HiOutlineGlobeAlt,
  HiOutlineUserPlus,
  HiOutlineUsers,
} from "react-icons/hi2";

const AffiliateContent = () => {
  const location = useLocation();
  const isOverviewPage = location.pathname === "/company/dashboard/affiliates";
  const setupSteps = [
    {
      title: "Invite Affiliate",
      description:
        "Invite new affiliate partners to your network. Set up their profiles and commission structures.",
      ctaLabel: "Invite",
      ctaPath: "/company/dashboard/affiliates/create",
      icon: HiOutlineEnvelope,
    },
    {
      title: "Add Affiliate",
      description:
        "Add new affiliate partners to your network. Set up their profiles and commission structures.",
      ctaLabel: "Add",
      ctaPath: "/company/dashboard/affiliates/add",
      icon: HiOutlineUserPlus,
    },
    {
      title: "View Affiliates",
      description:
        "View and manage all your affiliate partners. Track their performance and engagement.",
      ctaLabel: "View",
      ctaPath: "/company/dashboard/affiliates/",
      icon: HiOutlineUsers,
    },
    {
      title: "Affiliate Analytics",
      description:
        "Access detailed performance metrics and analytics for your affiliate network.",
      ctaLabel: "Analytics",
      ctaPath: "/company/dashboard/affiliates/analytics",
      icon: HiOutlineChartBar,
    },
    {
      title: "Affiliate Marketplace",
      description:
        "Explore a curated list of affiliate partners. Find the perfect fit for your brand.",
      ctaLabel: "Marketplace (Coming Soon)",
      icon: HiOutlineGlobeAlt,
      isDisabled: true,
    },
  ];

  // Helper function to truncate content
  const truncateText = (text, wordLimit) => {
    const words = text.split(" ");
    if (words.length > wordLimit) {
      return words.slice(0, wordLimit).join(" ") + "...";
    }
    return text;
  };

  if (!isOverviewPage) {
    return <Outlet />;
  }

  return (
    <div className="bg-gray-100 min-h-full w-full flex flex-col">
      <div className="flex flex-col lg:flex-row flex-1">
        <div className="flex-1 p-0 md:p-3 lg:p-4">
          <div className="bg-white border border-gray-100 shadow-sm p-2 mb-4 md:hidden">
            <h2 className="text-lg leading-7 font-bold mb-2">Affiliate Management</h2>
            <p className="text-xs text-gray-500 mb-4">
              Manage your affiliate partners and grow your network.
            </p>

            <div className="flex items-center justify-between mb-4">
              {setupSteps.map((_, index) => (
                <React.Fragment key={`affiliate-mobile-step-${index + 1}`}>
                  <span className="w-5 h-5 rounded-full bg-orange-500 text-white text-[11px] font-semibold flex items-center justify-center">
                    {index + 1}
                  </span>
                  {index < setupSteps.length - 1 && (
                    <HiArrowNarrowRight className="text-orange-500 text-lg shrink-0" />
                  )}
                </React.Fragment>
              ))}
            </div>

            <div className="space-y-3">
              {setupSteps.map((step) => {
                const StepIcon = step.icon;

                return (
                  <div
                    key={step.title}
                    className={`rounded-xl border border-gray-200 bg-white p-3 shadow-[0_1px_2px_rgba(16,24,40,0.06)] ${
                      step.isDisabled ? "opacity-70" : ""
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <span className="w-10 h-10 rounded-full bg-orange-50 text-orange-500 flex items-center justify-center shrink-0">
                        <StepIcon className="text-lg" />
                      </span>
                      <div className="flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <h3 className="text-base font-semibold leading-5">
                            {step.title}
                          </h3>
                          <HiChevronRight className="text-gray-300 text-lg shrink-0 mt-0.5" />
                        </div>
                        <p className="text-[10px] text-gray-500 mt-1.5 leading-4">
                          {truncateText(step.description, 18)}
                        </p>
                        <div className="mt-3 flex justify-end">
                          {step.isDisabled ? (
                            <button
                              disabled
                              className="inline-flex items-center justify-center rounded-md bg-gray-300 px-3 py-1.5 text-[11px] font-semibold text-gray-600 cursor-not-allowed"
                            >
                              {step.ctaLabel}
                            </button>
                          ) : (
                            <Link
                              to={step.ctaPath}
                              className="inline-flex items-center justify-center rounded-md bg-orange-500 px-3 py-1.5 text-[11px] font-semibold text-white"
                            >
                              {step.ctaLabel}
                            </Link>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="hidden md:block bg-white shadow-md rounded-lg p-4 lg:p-5 mb-10 lg:mb-0">
            <h2 className="text-lg font-bold mb-2">Affiliate Management</h2>
            <p className="text-[10px] md:text-sm text-gray-500 mb-5">
              Manage your affiliate partners and grow your network.
            </p>

            {/* Setup Steps */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">


              {/* Invite Affiliate */}
              <div className="p-5 border rounded-lg flex flex-col h-full">
                <h3 className="font-semibold mb-2">Invite Affiliate</h3>
                <p className="text-[10px] md:text-sm text-gray-500 mb-4 flex-1">
                  {truncateText(
                    "Invite new affiliate partners to your network. Set up their profiles and commission structures.",
                    20
                  )}
                </p>
                <Link to="/company/dashboard/affiliates/create" className="bg-orange-500 text-[10px] md:text-sm text-white text-center font-medium px-4 py-2 rounded-lg w-full mt-auto">
                  Invite
                </Link>
              </div>

              {/* Add Affiliate */}
              <div className="p-5 border rounded-lg flex flex-col h-full">
                <h3 className="font-semibold mb-2">Add Affiliate</h3>
                <p className="text-[10px] md:text-sm text-gray-500 mb-4 flex-1">
                  {truncateText(
                    "Add new affiliate partners to your network. Set up their profiles and commission structures.",
                    20
                  )}
                </p>
                <Link to="/company/dashboard/affiliates/add" className="bg-orange-500 text-[10px] md:text-sm text-white text-center font-medium px-4 py-2 rounded-lg w-full mt-auto">
                  Add
                </Link>
              </div>

              {/* View Affiliates */}
              <div className="p-5 border rounded-lg flex flex-col h-full">
                <h3 className="font-semibold mb-2">View Affiliates</h3>
                <p className="text-[10px] md:text-sm text-gray-500 mb-4 flex-1">
                  {truncateText(
                    "View and manage all your affiliate partners. Track their performance and engagement.",
                    20
                  )}
                </p>
                <Link to="/company/dashboard/affiliates/" className="bg-orange-500 text-[10px] md:text-sm text-white text-center font-medium px-4 py-2 rounded-lg w-full mt-auto">
                  View
                </Link>
              </div>

              {/* Affiliate Analytics */}
              <div className="p-5 border rounded-lg flex flex-col h-full">
                <h3 className="font-semibold mb-2">Affiliate Analytics</h3>
                <p className="text-[10px] md:text-sm text-gray-500 mb-4 flex-1">
                  {truncateText(
                    "Access detailed performance metrics and analytics for your affiliate network.",
                    20
                  )}
                </p>
                <Link to="/company/dashboard/affiliates/analytics" className="bg-orange-500 text-[10px] md:text-sm text-white text-center font-medium px-4 py-2 rounded-lg w-full mt-auto">
                  Analytics
                </Link>
              </div>

              {/* Affiliate Marketplace */}
              {/* <div className="p-5 border rounded-lg flex flex-col h-full">
                <h3 className="font-semibold mb-2">Affiliate Marketplace</h3>
                <p className="text-[10px] md:text-sm text-gray-500 mb-4 flex-1">
                  {truncateText(
                    "Explore a curated list of affiliate partners. Find the perfect fit for your brand.",
                    20
                  )}
                </p>
                <Link to="/company/dashboard/affiliates/marketplace" className="bg-orange-500 text-[10px] md:text-sm text-white text-center font-medium px-4 py-2 rounded-lg w-full mt-auto">
                  Marketplace
                </Link>
              </div> */}

              {/* Affiliate Marketplace */}
              <div className="p-5 border rounded-lg flex flex-col h-full opacity-60 cursor-not-allowed">
                <h3 className="font-semibold mb-2">Affiliate Marketplace</h3>

                <p className="text-[10px] md:text-sm text-gray-500 mb-4 flex-1">
                  {truncateText(
                    "Explore a curated list of affiliate partners. Find the perfect fit for your brand.",
                    20
                  )}
                </p>

                <button
                  disabled
                  className="bg-gray-300 text-[10px] md:text-sm text-gray-600 text-center font-medium px-4 py-2 rounded-lg w-full mt-auto cursor-not-allowed"
                >
                  Marketplace (Coming Soon)
                </button>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AffiliateContent;